import { gql } from '@apollo/client'
import { graphql } from '@/graphql-setup'

export const INFRINGEMENT_MARKERS_QUERY = graphql(`
  query InfringementMarkers(
    $userId: ID!
    $dateStart: DateTime!
    $dateEnd: DateTime!
  ) {
    infringementMarkers(
      userId: $userId
      dateStart: $dateStart
      dateEnd: $dateEnd
    ) {
      id
      infringementTimestamp
      severity
      categoryIdentifier
    }
  }
`)

export const INFRINGEMENT_QUERY = graphql(`
  query Infringement(
    $id: ID!
    $activitiesExtendBeforeMinutes: Int
    $activitiesExtendAfterMinutes: Int
  ) {
    infringement(id: $id) {
      id
      createdAt
      reasonValue

      periodStartDate
      periodEndDate
      evaluationWindowEnd

      infringementTimestamp
      valueKind
      valueMagnitudeMinutes
      recordedTotalMinutes
      limitMinutes

      relatedIssues {
        id
        closedAt
      }

      issueReason

      archivedBy {
        id
        fullName
      }
      archivedAt
      archivedBasis
      closedAt
      solution
      leadersComment
      attachments {
        edges {
          node {
            id
            name
            file
          }
        }
      }
      actionLogs {
        edges {
          node {
            id
            action
            createdAt
            user {
              id
              fullName
            }
            secondaryUser {
              id
              fullName
            }
          }
        }
      }
      user {
        id
        fullName
      }
      infringementCategory {
        id
        identifier
        lowerLimit
        upperLimit
        value
        severity
        description
        legalBases {
          edges {
            node {
              article
            }
          }
        }
      }
      activities(
        extendBeforeMinutes: $activitiesExtendBeforeMinutes
        extendAfterMinutes: $activitiesExtendAfterMinutes
      ) {
        id
        activityType
        datetimeStart
        datetimeEnd
        vehicleRegistrationNumber
        manuallyEntered
      }
    }
  }
`)

export const ALL_INFRINGEMENTS_QUERY = graphql(`
  query AllInfringements(
    $first: Int
    $after: String
    $users: [ID]
    $q: String
    $orderBy: String
    $includeStatuses: [InfringementStatus]
    $excludeSeverities: [String]
    $excludeCategories: [String]
    $infringementTimestamp_Lt: DateTime
    $infringementTimestamp_Gte: DateTime
    $leaderGroupId: ID
  ) {
    allInfringements(
      first: $first
      after: $after
      users: $users
      q: $q
      orderBy: $orderBy
      includeStatuses: $includeStatuses
      excludeSeverities: $excludeSeverities
      excludeCategories: $excludeCategories
      infringementTimestamp_Lt: $infringementTimestamp_Lt
      infringementTimestamp_Gte: $infringementTimestamp_Gte
      leaderGroupId: $leaderGroupId
    ) {
      pageInfo {
        endCursor
        hasNextPage
      }
      edges {
        node {
          id
          infringementCategory {
            id
            identifier
            severity
            description
          }
          infringementTimestamp
          archivedAt
          closedAt
          status
          user {
            id
            fullName
            phoneNumber
          }
          hasClosedIssue
          latestActionLog {
            id
            action
            user {
              id
              fullName
            }
            secondaryUser {
              id
              fullName
            }
            createdAt
          }
        }
      }
    }
  }
`)

export const USER_INFRINGEMENTS = gql`
  query UserInfringements(
    $user: ID!
    $dateGte: DateTime
    $dateLt: DateTime
    $orderBy: String
  ) {
    user(id: $user) {
      id
      fullName
      phoneNumber
      email
      notificationEmail
      infringementsTotalValue
      infringementCount
      minorInfringementCount
      seriousInfringementCount
      verySeriousInfringementCount
      mostSeriousInfringementCount
      userTypes {
        name
      }
    }
    allInfringements(
      user: $user
      infringementTimestamp_Gte: $dateGte
      infringementTimestamp_Lt: $dateLt
      orderBy: $orderBy
      archivedAt_Isnull: true
    ) {
      edges {
        node {
          id
          archivedAt
          infringementTimestamp
          infringementCategory {
            id
            identifier
            value
            severity
          }
        }
      }
    }
  }
`

export const ALL_USER_INFRINGEMENTS_QUERY = gql`
  query AllUsersInfringementsSums(
    $first: Int
    $after: String
    $q: String
    $orderBy: String
    $users: [ID]
    $dateGte: DateTime!
    $dateLt: DateTime!
  ) {
    allUsersInfringementsSums(
      first: $first
      after: $after
      q: $q
      orderBy: $orderBy
      users: $users
      infringementTimestamp_Gte: $dateGte
      infringementTimestamp_Lt: $dateLt
    ) {
      edges {
        node {
          id
          fullName
          phoneNumber
          infringementsTotalValue
          infringementCount
        }
      }
    }
  }
`

export const ALL_COMBINED_INFRINGEMENTS_QUERY = graphql(`
  query AllCombinedInfringements(
    $offset: Int
    $first: Int
    $sources: [CombinedInfringementSource!]
    $users: [ID]
    $q: String
    $orderBy: String
    $includeStatuses: [InfringementStatus]
    $excludeSeverities: [String]
    $excludeCategories: [String]
    $infringementTimestamp_Lt: DateTime
    $infringementTimestamp_Gte: DateTime
    $leaderGroupId: ID
  ) {
    allCombinedInfringements(
      offset: $offset
      first: $first
      sources: $sources
      users: $users
      q: $q
      orderBy: $orderBy
      includeStatuses: $includeStatuses
      excludeSeverities: $excludeSeverities
      excludeCategories: $excludeCategories
      infringementTimestampLt: $infringementTimestamp_Lt
      infringementTimestampGte: $infringementTimestamp_Gte
      leaderGroupId: $leaderGroupId
    ) {
      total
      hasMore
      rows {
        id
        source
        status
        infringement {
          id
          infringementCategory {
            id
            identifier
            severity
            description
          }
          infringementTimestamp
          archivedAt
          closedAt
          status
          user {
            id
            fullName
            phoneNumber
          }
          hasClosedIssue
          latestActionLog {
            id
            action
            user {
              id
              fullName
            }
            secondaryUser {
              id
              fullName
            }
            createdAt
          }
        }
        workInfringement {
          id
          infringementTimestamp
          archivedAt
          closedAt
          solution
          wronglyGenerated
          workInfringementCategory {
            id
            identifier
            title
          }
          user {
            id
            fullName
            phoneNumber
          }
          latestActionLog {
            id
            action
            attachmentName
            createdAt
            user {
              id
              fullName
            }
          }
        }
        tachographInfringement {
          id
          infringementTimestamp
          archivedAt
          closedAt
          solution
          wronglyGenerated
          category {
            id
            identifier
            title
            severity
          }
          user {
            id
            fullName
            phoneNumber
          }
          latestActionLog {
            id
            action
            attachmentName
            createdAt
            user {
              id
              fullName
            }
          }
        }
      }
    }
  }
`)

export const COMBINED_INFRINGEMENT_STATISTICS_QUERY = graphql(`
  query CombinedInfringementStatistics(
    $sources: [CombinedInfringementSource!]
    $users: [ID]
    $q: String
    $includeStatuses: [InfringementStatus]
    $excludeSeverities: [String]
    $excludeCategories: [String]
    $infringementTimestamp_Lt: DateTime
    $infringementTimestamp_Gte: DateTime
    $leaderGroupId: ID
  ) {
    combinedInfringementStatistics(
      sources: $sources
      users: $users
      q: $q
      includeStatuses: $includeStatuses
      excludeSeverities: $excludeSeverities
      excludeCategories: $excludeCategories
      infringementTimestampLt: $infringementTimestamp_Lt
      infringementTimestampGte: $infringementTimestamp_Gte
      leaderGroupId: $leaderGroupId
    ) {
      total
      drivers
      bySource {
        key
        count
      }
      byStatus {
        key
        count
      }
      bySeverity {
        key
        count
      }
    }
  }
`)
